import {useMutation, useQuery, UseQueryOptions} from '@tanstack/react-query';
import {
  addWorkshopUsers,
  createRecipe,
  createWorkshop,
  deleteRecipe,
  deleteWorkshop,
  deleteWorkshopUser,
  disconnectMiro,
  duplicateWorkshop,
  exportAsRecipe,
  getRecipe,
  getRecipeCategories,
  getRecipes,
  getWorkshop,
  getWorkshopAvailableUsers,
  getWorkshops,
  getWorkshopUsers,
  linkMiroBoardToWorkshop,
  miroBoardFromWorkshop,
  saveRecipe,
  saveWorkshop,
  toggleLikeRecipe,
  updateWorkshopUser,
  viewRecipe,
  viewWorkshop,
} from 'services/axios/modules';
import {queryClient} from '..';
import produce from 'immer';

export const useRecipeCategories = () => {
  return useQuery<ToolboxObject.WorkshopCategory[]>(
    ['recipeCategories'],
    () => {
      return getRecipeCategories();
    },
    {staleTime: 3600000, cacheTime: 3600000}
  );
};

export const useRecipes = () => {
  return useQuery(
    ['recipes'],
    () => {
      return getRecipes();
    },
    {cacheTime: 0}
  );
};

export const useWorkshops = () => {
  return useQuery(
    ['workshops'],
    () => {
      return getWorkshops();
    },
    {cacheTime: 0}
  );
};

export const useWorkshop = (id: number, recipe?: boolean, options: UseQueryOptions<ToolboxObject.Workshop> = {}) => {
  return useQuery<ToolboxObject.Workshop>(
    [recipe ? 'recipes' : 'workshops', id],
    () => {
      return recipe ? getRecipe(id) : getWorkshop(id);
    },
    {staleTime: 0, ...options}
  );
};

export const useViewWorkshop = (workshopId: number) => {
  return useMutation({
    mutationFn: () => {
      return viewWorkshop(workshopId);
    },
    onSuccess: (data) => {
      const {last_viewed} = data;
      return queryClient.setQueryData(['workshops'], (oldWorkshops: ToolboxObject.Workshop[] | undefined) => {
        return produce(oldWorkshops, (draft: ToolboxObject.Workshop[]) => {
          const index = draft?.findIndex((workshop) => {
            return workshop.id === workshopId;
          });
          if (index !== undefined && index !== -1 && draft) draft[index].user_data = {is_favorite: false, last_viewed};
        });
      });
    },
  });
};

export const useAddWorkshopUsers = (workshopId: number) => {
  return useMutation({
    mutationFn: (data: {userIds: number[]; access: ToolboxObject.WorkshopUserAccess}) => {
      return addWorkshopUsers(workshopId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workshops']);
    },
  });
};

export const useWorkshopUsers = (workshopId: number, options: UseQueryOptions<ToolboxObject.WorkshopUser[]> = {}) => {
  return useQuery<ToolboxObject.WorkshopUser[]>(['workshops', workshopId, 'users'], () => getWorkshopUsers(workshopId), options);
};

export const useUpdateWorkshopUser = (workshopId: number) => {
  return useMutation({
    mutationFn: (data: {access: ToolboxObject.WorkshopUserAccess; userId: number}) => {
      return updateWorkshopUser(workshopId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workshops', workshopId, 'users']);
    },
  });
};

export const useDeleteWorkshopUser = (workshopId: number) => {
  return useMutation({
    mutationFn: (userId: number) => {
      return deleteWorkshopUser(workshopId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workshops', workshopId, 'users']);
    },
  });
};

export const useWorkshopAvailableUsers = (workshopId: number, options: UseQueryOptions<ToolboxObject.User[]> = {}) => {
  return useQuery<ToolboxObject.User[]>(
    ['workshops', workshopId, 'users', 'available'],
    () => {
      return getWorkshopAvailableUsers(workshopId);
    },
    options
  );
};

export const useLikeRecipe = (recipeId: number, isLiked: boolean) => {
  return useMutation({
    mutationFn: () => {
      return toggleLikeRecipe(recipeId, isLiked);
    },
    onSuccess: (data) => {
      const {is_favorite, last_viewed} = data;
      return queryClient.setQueryData(['recipes'], (oldRecipes: ToolboxObject.Workshop[] | undefined) => {
        return produce(oldRecipes, (draft: ToolboxObject.Workshop[]) => {
          const index = draft?.findIndex((recipe) => {
            return recipe.id === recipeId;
          });
          if (index !== undefined && index !== -1 && draft) draft[index].user_data = {is_favorite, last_viewed};
        });
      });
    },
  });
};

export const useViewRecipe = (recipeId: number) => {
  return useMutation({
    mutationFn: () => {
      return viewRecipe(recipeId);
    },
    onSuccess: (data) => {
      const {is_favorite, last_viewed} = data;
      return queryClient.setQueryData(['recipes'], (oldRecipes: ToolboxObject.Workshop[] | undefined) => {
        return produce(oldRecipes, (draft: ToolboxObject.Workshop[]) => {
          const index = draft?.findIndex((recipe) => {
            return recipe.id === recipeId;
          });
          if (index !== undefined && index !== -1 && draft) draft[index].user_data = {is_favorite, last_viewed};
        });
      });
    },
  });
};

export const useSaveWorkshop = (isRecipe?: boolean) => {
  return useMutation({
    mutationFn: (data: {
      id: number;
      sessions: ToolboxObject.WorkshopSession[];
      title: string;
      description: string;
      requirements: ToolboxObject.Info[];
      benefits: ToolboxObject.Info[];
      category?: ToolboxObject.WorkshopCategory | null;
      teaser?: string | null;
      checklist: ToolboxObject.ChecklistTask[];
      language: ToolboxObject.Locale;
    }) => {
      const saveFunction = isRecipe ? saveRecipe : saveWorkshop;
      return saveFunction(data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([isRecipe ? 'recipes' : 'workshops']);
    },
  });
};

export const useMiroBoardFromWorkshop = (workshopId: number) => {
  return useMutation({
    mutationFn: () => {
      return miroBoardFromWorkshop(workshopId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['workshops']);
    },
  });
};

export const useDisconnectMiro = () => {
  return useMutation({
    mutationFn: () => {
      return disconnectMiro();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['me']);
    },
  });
};

export const useLinkMiroBoardToWorkshop = (workshopId: number) => {
  return useMutation({
    mutationFn: (href: string | null) => {
      return linkMiroBoardToWorkshop(workshopId, href);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['workshops']);
    },
  });
};

export const useCreateWorkshop = (isRecipe?: boolean) => {
  return useMutation({
    mutationFn: (data: {
      sessions: ToolboxObject.WorkshopSession[];
      title: string;
      description: string;
      requirements: ToolboxObject.Info[];
      benefits: ToolboxObject.Info[];
      category?: ToolboxObject.WorkshopCategory | null;
      teaser?: string | null;
      checklist: ToolboxObject.ChecklistTask[];
      language: ToolboxObject.Locale;
    }) => {
      const createFunction = isRecipe ? createRecipe : createWorkshop;
      return createFunction({
        ...data,
        category: isRecipe ? data.category ?? null : null,
        teaser: isRecipe ? data.teaser ?? null : null,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([isRecipe ? 'recipes' : 'workshops']);
    },
  });
};

export const useDuplicateWorkshop = (workshopId: number) => {
  return useMutation({
    mutationFn: (data: {title: string}) => {
      return duplicateWorkshop(workshopId, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['workshops']);
    },
  });
};

export const useExportAsRecipe = (workshopId: number) => {
  return useMutation({
    mutationFn: (data: {category: ToolboxObject.WorkshopCategory | null; teaser: string | null}) => {
      return exportAsRecipe(workshopId, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['workshops']);
      await queryClient.invalidateQueries(['recipes']);
    },
  });
};

export const useDeleteWorkshop = (isRecipe?: boolean) => {
  return useMutation({
    mutationFn: (data: {id: number; newOwnerId?: number}) => {
      const {id, newOwnerId} = data;
      return isRecipe ? deleteRecipe(id) : deleteWorkshop(id, {isRecipe: false, newOwnerId});
    },
  });
};
